// OpenSeatsPanel.tsx
// Raid leaders open seats in a raid to the public: how many, which specs,
// a minimum parse average, and whether a click seats the player at once or
// sends a request to approve here. Players who qualify see it on their Home.
// Server: auth/open_seats_routes.js (rules in auth/open_seats.js).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DISPLAY, Icon, tc } from "./alignmentUi";
import SpecPicker, { PickerOption } from "./SpecPicker";
import { getWowClassColor } from "./wowClassColor";
import ContractsEditor, { RaidContract } from "./ContractsEditor";

type Person = { id?: string; characterId: string; name: string; className: string | null; parse: number | null; spec: string | null; specImage: string | null; createdAt?: string; contractHeadline?: string | null };
type Config = { open: boolean; seats: number; specIds: number[]; minParse: number | null; autoAccept: boolean; lootMethod: string | null; openedAt: string | null };
type LootMethod = { key: string; label: string; desc: string };
type Data = { gameId: string; config: Config; seatsLeft: number; started: boolean; taken: Person[]; requests: Person[]; contracts?: RaidContract[]; lootMethods?: LootMethod[]; suggestedLootMethod?: string | null };

const ERRORS: Record<string, string> = {
  invalid_seats: "Pick between 1 and 40 seats.",
  loot_method_required: "Pick how loot is handed out. Players need to know before they join.",
  invalid_min_parse: "Minimum parse must be between 0 and 100.",
  unknown_spec: "One of those specs is not part of this game.",
  not_eligible: "They no longer qualify (locked out, booked elsewhere, or changed character). Decline instead.",
  raid_full: "The raid has no empty seat left.",
  seats_closed: "Seats are closed.",
  already_in_raid: "They are already in this raid.",
  contract_full: "Their contract has no holder place left. Raise its holders, or decline.",
};

export default function OpenSeatsPanel({
  raidId, canManage, isDirty, onSeated,
}: { raidId: string; canManage: boolean; isDirty: boolean; onSeated: () => void }) {
  const [data, setData] = useState<Data | null>(null);
  const [fields, setFields] = useState<Array<{ key: string; options: PickerOption[] }>>([]);
  const [seats, setSeats] = useState(1);
  const [specIds, setSpecIds] = useState<number[]>([]);
  const [minParse, setMinParse] = useState<string>("");
  const [autoAccept, setAutoAccept] = useState(true);
  const [lootMethod, setLootMethod] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/raids/${encodeURIComponent(raidId)}/open-seats`, { credentials: "include" });
      if (!r.ok) return;
      const d: Data = await r.json();
      setData(d);
      setSeats(d.config.seats || 1);
      setSpecIds(d.config.specIds || []);
      setMinParse(d.config.minParse === null ? "" : String(d.config.minParse));
      setAutoAccept(d.config.autoAccept);
      setLootMethod(d.config.lootMethod || d.suggestedLootMethod || "");
      if (d.config.open || d.requests.length) setExpanded(true);
    } catch { /* stays hidden */ }
  }, [raidId]);
  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  useEffect(() => {
    if (!data?.gameId) return;
    fetch(`/games/${encodeURIComponent(data.gameId)}/fields`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFields)
      .catch(() => setFields([]));
  }, [data?.gameId]);

  const classes = useMemo(() => fields.find((g) => g.key === "class")?.options || [], [fields]);
  const specs = useMemo(() => fields.find((g) => g.key === "spec")?.options || [], [fields]);
  const roles = useMemo(() => fields.find((g) => g.key === "role")?.options || [], [fields]);

  if (!canManage || !data) return null;
  const cfg = data.config;

  const save = async (open: boolean) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`/raids/${encodeURIComponent(raidId)}/open-seats`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ open, seats, specIds, minParse: minParse.trim() === "" ? null : Number(minParse), autoAccept, lootMethod: lootMethod || null }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(ERRORS[j.error] || j.error || "Something went wrong");
      await load();
      setMsg({ ok: true, text: open ? (cfg.open ? "Updated." : "Seats are open. Players who qualify see them now.") : "Seats closed. Waiting requests were cleared." });
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id: string, decision: "accept" | "decline") => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`/raids/${encodeURIComponent(raidId)}/open-seats/requests/${id}/${decision}`, { method: "POST", credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(ERRORS[j.error] || j.error || "Something went wrong");
      await load();
      if (decision === "accept") {
        if (isDirty) setMsg({ ok: true, text: "Seated. Save your roster changes, then reopen the raid to see them." });
        else onSeated();
      }
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleSpec = (id: number) => setSpecIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const specLabel = (id: number) => specs.find((s) => s.id === id);

  const PersonRow = ({ p, actions }: { p: Person; actions?: React.ReactNode }) => (
    <div className="flex items-center gap-2.5 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-1.5">
      <Icon src={p.specImage} alt={p.spec || p.className || p.name} size={26} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold" style={{ color: tc(getWowClassColor(p.className)) }}>{p.name}</div>
        <div className="text-[11px] text-[var(--c-ink-3)]">{[p.spec, p.className].filter(Boolean).join(" ")}{p.parse !== null ? ` · parse ${Math.round(p.parse)}` : " · no logs"}</div>
        {p.contractHeadline && <div className="text-[11px] font-semibold text-amber-800 dark:text-amber-200">📜 {p.contractHeadline}</div>}
      </div>
      {actions}
    </div>
  );

  return (
    <div className="mt-4 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 shadow-[var(--c-shadow-sm)]">
      <button type="button" onClick={() => setExpanded((e) => !e)} className="flex w-full items-center gap-2 text-left">
        <span className="text-[15px] text-[var(--c-ink)]" style={DISPLAY}>Open seats</span>
        {cfg.open ? (
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
            {data.started ? "Open · raid started" : `Open · ${data.seatsLeft} left`}
          </span>
        ) : (
          <span className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[11px] font-bold text-[var(--c-ink-3)]">Closed</span>
        )}
        {data.requests.length > 0 && (
          <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[11px] font-bold text-amber-800 dark:text-amber-200">{data.requests.length} request{data.requests.length === 1 ? "" : "s"}</span>
        )}
        <span className="ml-auto text-xs text-[var(--c-ink-4)]">{expanded ? "▾ hide" : "▸ show"}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          <p className="text-[13px] text-[var(--c-ink-3)]">
            Offer seats to any player who qualifies: right realm and faction, not locked to this raid this week and not booked at the same time. It closes when the raid starts.
          </p>

          <div className="flex flex-wrap items-end gap-4">
            <label className="text-[12px] font-semibold text-[var(--c-ink-2)]">
              Seats
              <input type="number" min={1} max={40} value={seats} onChange={(e) => setSeats(Math.max(1, Math.min(40, Number(e.target.value) || 1)))}
                className="mt-1 block w-20 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-1.5 text-[14px] text-[var(--c-ink)] outline-none focus:border-[var(--c-accent)]" />
            </label>
            <label className="text-[12px] font-semibold text-[var(--c-ink-2)]">
              Minimum parse <span className="font-normal text-[var(--c-ink-4)]">(optional)</span>
              <input type="number" min={0} max={100} value={minParse} placeholder="Any" onChange={(e) => setMinParse(e.target.value)}
                className="mt-1 block w-28 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-1.5 text-[14px] text-[var(--c-ink)] outline-none focus:border-[var(--c-accent)]" />
            </label>
            <label className="flex cursor-pointer items-center gap-2 pb-1.5 text-[13px] font-semibold text-[var(--c-ink-2)]">
              <input type="checkbox" checked={autoAccept} onChange={(e) => setAutoAccept(e.target.checked)} className="h-4 w-4 accent-amber-500" />
              Auto-accept
              <span className="font-normal text-[var(--c-ink-4)]">{autoAccept ? "(a click seats them)" : "(you approve each request)"}</span>
            </label>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">
              Loot method <span className="normal-case tracking-normal text-[var(--c-bad-ink)]">required</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(data.lootMethods || []).map((m) => (
                <button key={m.key} type="button" title={m.desc} aria-pressed={lootMethod === m.key} onClick={() => setLootMethod(m.key)}
                  className={`rounded-lg border px-3 py-1.5 text-[12px] font-semibold transition ${lootMethod === m.key ? "border-[var(--c-accent)] bg-[var(--c-sunken)] text-[var(--c-ink)]" : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            {lootMethod && <p className="mt-1 text-[12px] text-[var(--c-ink-4)]">{(data.lootMethods || []).find((m) => m.key === lootMethod)?.desc}</p>}
          </div>

          {minParse.trim() !== "" && (
            <p className="-mt-2 text-[12px] text-[var(--c-ink-4)]">Players without logs will not see these seats.</p>
          )}

          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Specs</span>
              <span className="text-[11px] text-[var(--c-ink-4)]">{specIds.length ? `${specIds.length} picked` : "Any spec · pick to lock"}</span>
            </div>
            {specIds.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {specIds.map((id) => {
                  const s = specLabel(id);
                  const c = classes.find((x) => x.key === s?.metadata?.class_key);
                  return s ? (
                    <button key={id} type="button" onClick={() => toggleSpec(id)} title="Remove"
                      className="flex items-center gap-1.5 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] py-0.5 pl-0.5 pr-2 text-[12px] font-semibold">
                      <Icon src={s.image_url} alt={s.label} size={20} />
                      <span style={{ color: tc(c?.metadata?.color || undefined) }}>{s.label} {c?.label}</span>
                      <span className="text-[var(--c-ink-4)]">×</span>
                    </button>
                  ) : null;
                })}
              </div>
            )}
            <SpecPicker classes={classes} specs={specs} selected={specIds} onToggle={toggleSpec} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => save(true)} disabled={busy || !lootMethod} title={lootMethod ? undefined : "Pick a loot method first"}
              className="rounded-xl bg-[var(--c-accent)] px-4 py-2 text-[13px] font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)] disabled:opacity-50 dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500">
              {cfg.open ? "Update" : "Open to the public"}
            </button>
            {cfg.open && (
              <button type="button" onClick={() => save(false)} disabled={busy}
                className="rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-4 py-2 text-[13px] font-semibold text-[var(--c-ink)] transition hover:bg-[var(--c-sunken-2)] disabled:opacity-50">
                Close seats
              </button>
            )}
            {msg && <span className={`text-[13px] ${msg.ok ? "text-emerald-700 dark:text-emerald-400" : "text-[var(--c-bad-ink)]"}`}>{msg.text}</span>}
          </div>

          <ContractsEditor
            raidId={raidId}
            contracts={data.contracts || []}
            classes={classes}
            specs={specs}
            roles={roles}
            onChanged={load}
          />

          {data.requests.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Requests</div>
              <div className="space-y-1.5">
                {data.requests.map((p) => (
                  <PersonRow key={p.id} p={p} actions={
                    <div className="flex gap-1.5">
                      <button type="button" disabled={busy} onClick={() => decide(p.id!, "accept")}
                        className="rounded-lg bg-emerald-600 px-2.5 py-1 text-[12px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50">Accept</button>
                      <button type="button" disabled={busy} onClick={() => decide(p.id!, "decline")}
                        className="rounded-lg border border-[var(--c-line)] px-2.5 py-1 text-[12px] font-semibold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)] disabled:opacity-50">Decline</button>
                    </div>
                  } />
                ))}
              </div>
            </div>
          )}

          {data.taken.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Joined through open seats</div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {data.taken.map((p) => <PersonRow key={p.characterId} p={p} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
